import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { EditorialBadge, EditorialDataTable, type EditorialBadgeTone } from '../../report/editorial';

/**
 * Effectiveness tier → EditorialBadge tone (replaces filled chip).
 * LLM output may append qualifiers (e.g. "High (Regulatory Requirement)");
 * tier is inferred from the prefix, mirroring the prior chip logic.
 */
function effectivenessTone(effectiveness: string): EditorialBadgeTone {
  const key = effectiveness.trim().toLowerCase();
  if (key.startsWith('high')) return 'positive';
  if (key.startsWith('medium')) return 'warning';
  return 'neutral';
}

/**
 * W2d editorial port — the ad-hoc `<table>` (hand-rolled small-caps
 * header + an app-token mono utility on the cost cell, the dir's last
 * mono leak) becomes EditorialDataTable: mono small-caps header over the ink
 * underline, hairline rows, the cost column in the report body face
 * with tabular figures. Columns, authored strings and the
 * effectiveness EditorialBadge are unchanged (whiteSpace:normal so
 * sentence-length qualifiers wrap instead of overflowing in print).
 */
export const MitigationPlanSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const rows = report.mitigationPlan ?? [];
  const hasData = rows.length > 0;
  return (
    <SectionShell label={label} hasData={hasData} report={report}>
      {/* data-span-columns: the overflow-x-auto wrapper is a BFC that would
          otherwise trap this wide 5-col table in a single flow column (crushing
          it to half-width in 2-col presets). The hook lets it span both.
          Carrier div holds the testid (EditorialDataTable has no testid prop). */}
      <div data-span-columns className="overflow-x-auto" data-testid="mitigation-plan-table">
        <EditorialDataTable
          theme={theme}
          columns={[
            { header: 'Impact' },
            { header: 'Mitigation Measure' },
            { header: 'Cost', numeric: true },
            { header: 'Timeline' },
            { header: 'Effectiveness' },
          ]}
          rows={rows.map(m => [
            m.impactCategory,
            m.measure,
            m.costEstimate,
            m.timeline,
            <EditorialBadge
              theme={theme}
              tone={effectivenessTone(m.effectiveness)}
              variant="outline"
              style={{ whiteSpace: 'normal' }}
            >
              {m.effectiveness}
            </EditorialBadge>,
          ])}
        />
      </div>
    </SectionShell>
  );
};
